import type { JSONSchema7 } from 'json-schema';

export type AzureEntraUserPickerReturnFormat = 'email' | 'object';

export type AzureEntraUserPickerUiOptions = {
  multiple?: boolean;
  /** Return a one-item array from a single-select picker to match an array schema. */
  returnArray?: boolean;
  autoSelect?: boolean;
  /** Prefill an empty single-select field with the signed-in user's Entra account. */
  defaultToCurrentUser?: boolean;
  allowArbitraryValues?: boolean;
  minQueryLength?: number;
  maxOptions?: number;
  debounceMs?: number;
  placeholder?: string;
  /** Prefill from an annotation on the entity selected in another form field. */
  entityField?: string;
  annotation?: string;
  /**
   * Value written into the scaffolder parameter.
   * `email` (default) keeps the current string / string[] contract.
   * `object` returns `{ id, email, displayName }` (or an array of those), where
   * `id` is always the Entra object id. Manually typed values are therefore not
   * allowed in this mode and `allowArbitraryValues` is ignored.
   */
  returnValue?: AzureEntraUserPickerReturnFormat;
};

export type AzureEntraUserPickerUiSchema = {
  'ui:options'?: AzureEntraUserPickerUiOptions;
  'ui:disabled'?: boolean;
  'ui:description'?: string;
};

export type AzureEntraUser = {
  id: string;
  email: string;
  displayName: string;
};

export type AzureEntraUserPickerValue =
  | string
  | string[]
  | AzureEntraUser
  | AzureEntraUser[];

export type AzureEntraUserPickerProps = {
  onChange: (value: AzureEntraUserPickerValue | undefined) => void;
  schema: {
    title?: string;
    description?: string;
  };
  required?: boolean;
  uiSchema: AzureEntraUserPickerUiSchema;
  rawErrors?: string[];
  formData?: AzureEntraUserPickerValue;
  formContext?: { formData?: Record<string, unknown> };
  idSchema?: { $id?: string };
};

export const AzureEntraUserPickerSchema = {
  uiOptions: {
    type: 'object',
    properties: {
      multiple: {
        type: 'boolean',
        title: 'Allow selecting multiple emails',
      },
      returnArray: {
        type: 'boolean',
        title: 'Return a one-item array in single-select mode',
      },
      autoSelect: {
        type: 'boolean',
        title: 'Auto-select when exactly one option is available',
      },
      defaultToCurrentUser: {
        type: 'boolean',
        title: 'Default to the signed-in user when the field is empty',
      },
      allowArbitraryValues: {
        type: 'boolean',
        title:
          'Allow manual email input that is not in the options list (ignored when returnValue is object)',
      },
      minQueryLength: {
        type: 'number',
        title: 'Minimum query length before search starts',
      },
      maxOptions: {
        type: 'number',
        title: 'Maximum number of options to display',
      },
      debounceMs: {
        type: 'number',
        title: 'Debounce delay for search input',
      },
      placeholder: {
        type: 'string',
        title: 'Custom input placeholder',
      },
      entityField: {
        type: 'string',
        title: 'Entity field',
        description: 'Form field containing the selected catalog entity',
      },
      annotation: {
        type: 'string',
        title: 'Annotation',
        description: 'Entity annotation used to prefill the selected email',
      },
      returnValue: {
        type: 'string',
        title: 'Template parameter format',
        description:
          'email (default) returns the selected email. object returns { id, email, displayName } where id is the Entra object id; manual (free-text) values are not allowed in object mode.',
        enum: ['email', 'object'],
        default: 'email',
      },
    },
  } as JSONSchema7,
  returnValue: {
    title: 'Selected Azure Entra user',
    oneOf: [
      {
        type: 'string',
      },
      {
        type: 'array',
        items: {
          type: 'string',
        },
      },
      {
        type: 'object',
        required: ['id', 'email', 'displayName'],
        properties: {
          id: { type: 'string' },
          email: { type: 'string' },
          displayName: { type: 'string' },
        },
      },
      {
        type: 'array',
        items: {
          type: 'object',
          required: ['id', 'email', 'displayName'],
          properties: {
            id: { type: 'string' },
            email: { type: 'string' },
            displayName: { type: 'string' },
          },
        },
      },
    ],
  } as JSONSchema7,
};
